import { Callout, CalloutTitle } from '../components/callout'
import type { Route } from './+types/mcp-apps'

export const meta = (): Route.MetaDescriptors => [
  { title: 'MCP Apps | Vital Care Finder' },
  {
    name: 'description',
    content:
      'The Vital Care Finder MCP Server connects AI assistants like ChatGPT and Claude to provider search, so you can find, compare and call US care providers without leaving the conversation.',
  },
]

export const headers: Route.HeadersFunction = () => ({
  'Cache-Control': 'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400',
})

function ParamTable({ rows }: { rows: { name: string; required: boolean; description: string }[] }) {
  return (
    <div className="overflow-x-auto rounded-2xl border not-prose">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="bg-slate-100">
            <th className="px-4 py-3 font-semibold">Parameter</th>
            <th className="px-4 py-3 font-semibold">Required</th>
            <th className="px-4 py-3 font-semibold">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr className="border-t align-top" key={row.name}>
              <td className="whitespace-nowrap px-4 py-3 font-mono font-bold">{row.name}</td>
              <td className="px-4 py-3">{row.required ? 'Yes' : 'No'}</td>
              <td className="px-4 py-3">{row.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const FIND_DOCTOR_PARAMS = [
  {
    name: 'specialty',
    required: true,
    description:
      'A medical specialty as clinical terminology (e.g. "Cardiovascular Disease Physician"). Your assistant translates everyday phrasing like "heart doctor" before calling.',
  },
  {
    name: 'searchLocation',
    required: true,
    description:
      'Where the user wants to find care, as a US ZIP code ("90210") or city with state ("Phoenix, AZ"). Not necessarily the user\'s current location.',
  },
  {
    name: 'insurancePlanId',
    required: false,
    description: 'A specific plan confirmed via find_insurance_plan, used to match providers at the plan level.',
  },
  {
    name: 'insuranceCarrier',
    required: false,
    description:
      'The user\'s insurance carrier name (e.g. "Aetna", "UnitedHealthcare", "Anthem Blue Cross Blue Shield"), used when no specific plan has been confirmed. Carrier only - not the plan name or type. Omitted for self-pay or uninsured.',
  },
  {
    name: 'clinicalContext',
    required: false,
    description:
      'The reason for the appointment (condition, symptoms, treatment sought), used to check fit against the conditions a provider treats.',
  },
  {
    name: 'patientAge',
    required: false,
    description:
      "The patient's age, used to drop age-inappropriate practices - for example, pediatric-only clinics for adult patients or geriatric-only practices for younger patients.",
  },
]

const FIND_INSURANCE_PLAN_PARAMS = [
  {
    name: 'carrier',
    required: true,
    description:
      'The user\'s insurance carrier name only (e.g. "Aetna", "Humana", "Anthem Blue Cross Blue Shield", "UnitedHealthcare", "Cigna"). Never includes the plan name or plan type.',
  },
  {
    name: 'planName',
    required: false,
    description:
      'A specific plan name and/or plan type, if the user mentioned one (e.g. "PPO", "AARP Medicare Advantage", "Choice Plus HMO").',
  },
  {
    name: 'state',
    required: false,
    description: 'A US state as a 2-letter USPS code (e.g. "TX", "CA"), used to prioritise plans issued there.',
  },
  {
    name: 'offset',
    required: false,
    description: 'Fetches the next batch of candidate plans if none of the first matches.',
  },
]

const MCP_JSON_SNIPPET = `{
  "mcpServers": {
    "vital-care-finder": {
      "url": "https://mcp.vital.io"
    }
  }
}`

export default function McpApps() {
  return (
    <>
      <header className="space-y-3">
        <h1>Vital Care Finder MCP Server</h1>
        <p>
          The Vital Care Finder MCP Server lets AI assistants help you find care. It connects assistants like ChatGPT
          and Claude to Vital's provider search through the{' '}
          <a className="underline" href="https://modelcontextprotocol.io" rel="noreferrer" target="_blank">
            Model Context Protocol (MCP)
          </a>{' '}
          - an open standard that lets AI applications call external services.
        </p>
      </header>
      <section>
        <p>
          Ask your assistant to find a care provider, and it can search Vital Care Finder for providers near you,
          matched against your insurance if you share it - returning an interactive card with ranked providers you can
          compare and call directly, without leaving the conversation. Vital Care Finder is available for{' '}
          <strong>US locations only</strong>.
        </p>
      </section>

      <section id="tools">
        <h2>Available tools</h2>
        <p>
          The server exposes two tools. Your AI assistant decides when to call them and extracts the parameters below
          from your conversation - you don't fill anything in yourself. The tables describe what each tool receives so
          you can see what data your assistant sends to Vital.
        </p>
        <h3 className="font-mono">find_doctor</h3>
        <p>
          Searches for care providers matching a specialty near a US location. Returns up to 8 ranked providers - each
          with name and credentials, specialty, distance, phone number, insurance-match signal, star rating, and
          treatment focus - rendered as an interactive card with a map and tap-to-call.
        </p>
        <ParamTable rows={FIND_DOCTOR_PARAMS} />
        <h3 className="font-mono">find_insurance_plan</h3>
        <p>
          Matches a plain-language description of your health insurance to specific plans in Vital's insurance catalog,
          so find_doctor can match providers who accept your insurance at the plan level. Returns up to 10 ranked
          candidate plans - each with carrier, plan name, plan type, and state - for you to confirm against your
          insurance card.
        </p>
        <ParamTable rows={FIND_INSURANCE_PLAN_PARAMS} />
      </section>

      <section id="connect">
        <h2>How to connect</h2>
        <h3>ChatGPT</h3>
        <p>
          Vital Care Finder is available in the ChatGPT Plugin Directory. Search for <strong>Vital Care Finder</strong>{' '}
          in ChatGPT's plugins and connect it - then just ask ChatGPT to help you find a doctor.
        </p>
        <h3>Claude</h3>
        <p>
          Vital Care Finder is available in the Claude Connectors directory at{' '}
          <a className="underline" href="https://claude.com" rel="noreferrer" target="_blank">
            claude.com
          </a>
          . Add it from <strong>Settings → Connectors</strong>, then ask Claude to find a doctor near you.
        </p>
        <h3>Other MCP clients</h3>
        <p>Any client that supports MCP over Streamable HTTP can connect using this configuration:</p>
        <pre className="overflow-x-auto rounded-2xl border p-4 font-mono">
          <code>{MCP_JSON_SNIPPET}</code>
        </pre>
        <p>
          The server uses a <strong>stateless transport</strong> with <strong>no login required</strong> - you can
          search without signing in.
        </p>
      </section>

      <section id="good-to-know">
        <h2>Good to know</h2>
        <ul>
          <li>
            <strong>US only.</strong> Searches outside the United States return an error.
          </li>
          <li>
            <strong>Search only.</strong> The tools are read-only: they search and display providers. No bookings are
            made, and nothing is written on your behalf. Calls are placed by your own phone direct to the clinic - Vital
            is not on the line.
          </li>
          <li>
            <strong>Not medical advice.</strong> Vital Care Finder is a provider directory, not a source of diagnosis or
            treatment recommendations. <strong>If this is an emergency, call 911.</strong>
          </li>
          <li>
            <strong>Confirm with the clinic.</strong> Provider details - including insurance network status, ratings,
            and availability - can change. Always confirm with the clinic before your visit.
          </li>
        </ul>
      </section>

      <section id="privacy">
        <h2>Privacy</h2>

        <Callout>
          <CalloutTitle level="3">What Vital receives</CalloutTitle>
          <p>
            Vital receives only the tool parameters your assistant sends - the specialty, search location, any insurance
            information, an optional clinical context, and an optional patient age.{' '}
            <strong>We never receive your chat history</strong>, your account details, or your identity.
          </p>
        </Callout>

        <p>
          See our{' '}
          <a className="underline" href="/privacy">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a className="underline" href="/terms">
            Terms of Service
          </a>
          .
        </p>
      </section>

      <section id="support">
        <h2>Support</h2>
        <p>
          Questions or feedback? Visit our{' '}
          <a className="underline" href="/support">
            support page
          </a>
          .
        </p>
        <p>
          For more on the Model Context Protocol, see the{' '}
          <a
            className="underline"
            href="https://modelcontextprotocol.io/specification/2025-11-25"
            rel="noreferrer"
            target="_blank"
          >
            MCP specification
          </a>
          .
        </p>
      </section>
    </>
  )
}
